import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  DeckCard,
  SwipeInput,
  SwipeResult,
  MatchSummary,
  MessageItem,
  SubscriptionStatus,
} from "@parble/types";

export function useDeck() {
  return useQuery({
    queryKey: ["deck"],
    queryFn: () => api.get<DeckCard[]>("/api/deck"),
    refetchOnWindowFocus: false,
  });
}

export function useMarkSeen() {
  return useMutation({
    mutationFn: (entryId: string) =>
      api.post<{ ok: true }>("/api/deck/seen", { entryId }),
  });
}

export function useSwipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SwipeInput) =>
      api.post<SwipeResult>("/api/swipe", input),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["profile", "me"] });
      if (result.matched) {
        qc.invalidateQueries({ queryKey: ["matches"] });
      }
    },
  });
}

export function useMatches() {
  return useQuery({
    queryKey: ["matches"],
    queryFn: () => api.get<MatchSummary[]>("/api/matches"),
    refetchInterval: 30_000,
  });
}

export function useUnmatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => api.delete(`/api/matches/${matchId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["matches"] }),
  });
}

export function useMessages(matchId: string) {
  return useQuery({
    queryKey: ["messages", matchId],
    queryFn: () => api.get<MessageItem[]>(`/api/messages/${matchId}`),
    refetchInterval: 5_000,
    enabled: Boolean(matchId),
  });
}

export function useSendMessage(matchId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      api.post<MessageItem>(`/api/messages/${matchId}`, { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", matchId] });
      qc.invalidateQueries({ queryKey: ["matches"] });
    },
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: ["subscription"],
    queryFn: () => api.get<SubscriptionStatus>("/api/subscription"),
  });
}

export function useCheckoutUrl() {
  return useMutation({
    mutationFn: () =>
      api.post<{ url: string }>("/api/subscription/checkout"),
  });
}

export function usePortalUrl() {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>("/api/subscription/portal"),
  });
}

export function useReportUser() {
  return useMutation({
    mutationFn: (input: {
      reportedId: string;
      reason: "fake" | "offensive" | "underage" | "spam" | "other";
      note?: string;
    }) => api.post<{ ok: true }>("/api/reports", input),
  });
}

export function useBlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (targetId: string) =>
      api.post<{ ok: true }>("/api/blocks", { targetId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["matches"] });
      qc.invalidateQueries({ queryKey: ["deck"] });
    },
  });
}
